import React, { useMemo, useState } from 'react';
// @ts-ignore
import { emojify } from 'react-emojione';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, WithStyles, withStyles } from '@material-ui/core';
import classnames from 'classnames';
import sanitizeHtml from 'sanitize-html';

import { Button } from './Button';
import { Dialog } from './Dialog';
import { DialogActions } from './DialogActions';
import { DialogContent } from './DialogContent';
import { DialogTitle } from './DialogTitle';
import { TypographyProps, Typography } from './Typography';

const messages = defineMessages({
  dialogCloseBtn: {
    id: 'embeds.components.TypographyMessageText.dialogCloseBtn',
    defaultMessage: 'Close',
  },
  dialogTitle: {
    id: 'embeds.components.TypographyMessageText.dialogTitle',
    defaultMessage: 'Message',
  },
});

const styles = createStyles({
  clickable: {
    cursor: 'pointer',
  },
});

export interface TypographyMessageTextProps
  extends WithStyles<typeof styles>,
    Omit<TypographyProps, 'classes' | 'onClick' | 'dangerouslySetInnerHTML' | 'children'> {
  messageText: string;
  includePopper?: boolean;
  dialogTitle?: string;
}

const TypographyMessageText: React.FC<TypographyMessageTextProps> = ({
  classes,
  className,
  messageText,
  includePopper,
  dialogTitle = 'Message',
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();

  const text = useMemo(
    () =>
      (messageText || '')
        .replace(
          /(:[\w]+:)/gm,
          (match: string) => `<span style="font-size: 14px;">${emojify(match, { output: 'unicode' })}</span>`,
        )
        .replace(/&nbsp;/g, '')
        // Bold tag <b></b>
        .replace(/<\s*b[^>]*>/g, '')
        .replace(/<\s*\/\s*b\s*>/g, ' ')
        // Paragraph tag <p></p>
        .replace(/<\s*p[^>]*>/g, '')
        .replace(/<\s*\/\s*p\s*>/g, ' ')
        // Anchor tag <a></a>
        .replace(/<\s*a[^>]*>/g, '')
        .replace(/<\s*\/\s*a\s*>/g, ' ')
        // Italic tag <i></i>
        .replace(/<\s*i[^>]*>/g, '')
        .replace(/<\s*\/\s*i\s*>/g, ' '),
    [messageText],
  );

  const htmlPopper = useMemo(
    () =>
      !includePopper
        ? ''
        : // Transform anchor tags from html string into span tag
          sanitizeHtml(messageText || '', { transformTags: { a: 'span' } }),
    [includePopper, messageText],
  );

  return (
    <>
      <Typography
        className={classnames({ [classes.clickable]: includePopper }, className)}
        onClick={() => includePopper && setOpen(true)}
        {...rest}
      >
        {text}
      </Typography>
      {htmlPopper && (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent dangerouslySetInnerHTML={{ __html: htmlPopper }} />
          <DialogActions>
            <Button
              data-lh-id="typography-message-close-dialog-btn"
              color="primary"
              rounded
              onClick={() => setOpen(false)}
            >
              <Typography variant="bW600F12">{formatMessage(messages.dialogCloseBtn)}</Typography>
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default withStyles(styles)(TypographyMessageText);
